import { networkClient } from './client';
import type { ApiCollection, ApiEnvelope, ApiPaginatedCollection } from './types';

export interface SuspensionDetails {
  type: string | null;
  startDate: string | null;
  endDate: string | null;
  description: string | null;
}

export interface InjuryDetails {
  type: string | null;
  startDate: string | null;
  endDate: string | null;
  expectedEndDate: string | null;
  status: string | null;
}

export interface NextFixture {
  fixtureId: string;
  opponentId: string;
  opponentName: string;
  opponentShortName: string | null;
  contestantFlagKey: string | null;
  kickoffAt: string;
  gameweek?: number;
  matchday?: number;
  venue: string | null;
  competitionName: string | null;
  isHome: boolean;
  matchDetailAvailable?: boolean;
}

export interface CurrentPeriodFixture {
  matchId: string;
  opponentId: string;
  opponentName: string;
  opponentShortName: string | null;
  contestantFlagKey: string | null;
  isHome: boolean;
  points: number | null;
  goalBonusNormalPoints?: number | null;
  goals?: number | null;
  showDNP?: boolean;
  gameweek?: number;
  matchday?: number;
  status: string;
  kickoffAt: string;
  competitionName: string | null;
}

export interface BEPlayerStatsItem {
  playerId: string;
  contestantId: string | null;
  firstName: string;
  lastName: string;
  shortLastName?: string | null;
  matchName?: string | null;
  displayName: string;
  position: string | null;
  shirtKey: string | null;
  contestantFlagKey: string | null;
  contestantName: string | null;
  contestantShortName: string | null;
  percentSelected: number;
  status: string | null;
  suspensionDetails: SuspensionDetails | null;
  injuryDetails: InjuryDetails | null;
  price: number;
  gameweekPoints?: number;
  matchdayPoints?: number;
  averagePoints: number;
  last3Average: number;
  ppmPoints: number;
  bonusPoints: number;
  totalPoints: number;
  goals: number;
  assists: number;
  shotsOnTarget: number;
  chancesCreated: number;
  tackles: number;
  cleanSheet: number;
  saves: number;
  goalsConceded: number;
  yellowCards: number;
  redCards: number;
  ownGoals: number;
  penaltyMisses: number;
  penaltySaves: number;
  bonusPpm: number;
  dribbles: number;
  crosses: number;
  offsides: number;
  passCompletionRate: number;
  interceptions: number;
  blocks: number;
  foulsWon: number;
  foulsMade: number;
  goalsOutsideArea: number;
  errorsLeadingToGoal: number;
  punches: number;
  claims: number;
  keeperSweeps: number;
  nextGameweekFixtures: NextFixture[];
}

export type PlayerStatsResponse = ApiEnvelope<ApiPaginatedCollection<BEPlayerStatsItem>>;

export interface FetchPlayerStatsParams {
  page?: number;
  limit?: number;
  search?: string;
  positions?: string;
  contestantIds?: string;
  priceRanges?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  tournamentCalendarId?: string;
  type?: 'gameweek' | 'matchday';
}

export const fetchPlayerStats = (params?: FetchPlayerStatsParams): Promise<PlayerStatsResponse> => {
  return networkClient
    .get<PlayerStatsResponse>('/api/players/stats', { params })
    .then(({ data }) => data);
};

export interface TeamFixture {
  opponentId: string;
  opponentName: string;
  opponentShortName: string;
  opponentFlagKey: string | null;
  kickoffAt: string;
  homeAway: 'H' | 'A';
  status?: 'playing' | 'fixture' | 'played' | null;
  /** Source competition for a fixture adopted from another competition (cup / European). */
  competitionName?: string | null;
  adopted?: boolean;
}

export interface FixtureTeam {
  id: string;
  name: string;
  shortName: string;
  fixtures: Record<string, TeamFixture[]>;
  /** True for EPL sides; false for cup / European opponents that only appear via adopted fixtures. */
  primary?: boolean;
}

export interface FixturesData {
  gameWeeks?: string[];
  matchDays?: string[];
  teams: FixtureTeam[];
}

export type FixturesResponse = ApiEnvelope<FixturesData>;

export interface FetchFixturesParams {
  fromGameweek?: number;
  toGameweek?: number;
  fromMatchday?: number;
  toMatchday?: number;
  teamId?: string;
  type?: 'gameweek' | 'matchday';
  // Fixtures Calendar only: exclude fixtures adopted from another competition
  // (league-cup / European ties under the EPL umbrella). Defaults off on the
  // backend, so team-building / player-selection consumers are unaffected.
  eplOnly?: boolean;
}

export const fetchFixtures = (params?: FetchFixturesParams): Promise<FixturesResponse> => {
  return networkClient
    .get<FixturesResponse>('/api/matches/fixtures', { params })
    .then(({ data }) => data);
};

export interface BEPlayerListItem {
  id: string;
  squadId: string | null;
  teamName: string | null;
  contestantName: string | null;
  contestantFlagKey: string | null;
  firstName: string;
  lastName: string;
  shortLastName?: string | null;
  matchName?: string | null;
  displayName: string;
  shirtKey: string | null;
  position: string | null;
  status: string | null;
  price: number;
  totalPoints: number;
  averagePoints: number;
  gameweekPoints?: number;
  matchdayPoints?: number;
  goalBonusNormalPoints?: number | null;
  ppmPoints: number;
  bonusPoints: number;
  last3Average: number;
  percentSelected: number;
  priceChangeUp: number;
  priceChangeDown: number;
  nationality: string | null;
  availabilityDisplay: string | null;
  optaPersonId: string | null;
  dreamTeamName: string | null;
  dreamTeamPrice: number | null;
  currentPeriodFixtures: CurrentPeriodFixture[];
  nextFixtures: NextFixture[];
  isVisible: boolean;
  hasLeft: boolean;
  suspensionDetails: {
    type: string | null;
    startDate: string | null;
    endDate: string | null;
    description: string | null;
  } | null;
  injuryDetails: {
    type: string | null;
    startDate: string | null;
    endDate: string | null;
    expectedEndDate: string | null;
    status: string | null;
  } | null;
}

export interface MatchdayStats {
  stats: {
    goals: number | null;
    assists: number | null;
    fantasyAssists: number | null;
    shotsOnTarget: number | null;
    chancesCreated: number | null;
    tackles: number | null;
    cleanSheet: boolean | null;
    saves: number | null;
    goalsConceded: number | null;
    yellowCards: number | null;
    redCards: number | null;
    ownGoals: number | null;
    penaltyMisses: number | null;
    penaltySaves: number | null;
    bonusPpm: number | null;
    dribbles: number | null;
    crosses: number | null;
    offsides: number | null;
    passCompletionRate: number | null;
    interceptions: number | null;
    blocks: number | null;
    foulsWon: number | null;
    foulsMade: number | null;
    goalsOutsideArea: number | null;
    errorsLeadingToGoal: number | null;
    punches: number | null;
    claims: number | null;
    keeperSweeps: number | null;
    minutesPlayed: number | null;
  };
  breakdown: {
    appearance?: number;
    goals?: number;
    goals_bonus?: number;
    assists?: number;
    fantasy_assists?: number;
    shots_on_target?: number;
    big_chances_created?: number;
    tackles?: number;
    yellow_cards?: number;
    red_cards?: number;
    penalty_misses?: number;
    own_goals?: number;
    saves?: number;
    penalty_saves?: number;
    clean_sheet?: number;
    goals_conceded?: number;
    bonus_points?: number;
    ppms_earned?: number;
    ppm_successful_dribble?: number;
    ppm_accurate_cross?: number;
    ppm_interception?: number;
    ppm_block?: number;
    ppm_goal_outside_area?: number;
    ppm_foul_won?: number;
    ppm_offside?: number;
    ppm_foul_conceded?: number;
    ppm_error_leading_to_goal?: number;
    ppm_successful_claim?: number;
    ppm_successful_punch?: number;
    ppm_keeper_sweep?: number;
    ppm_pass_completion_90?: number;
    ppm_pass_completion_80?: number;
    ppm_pass_completion_70?: number;
  };
  points: number | null;
  ppmPoints: number | null;
  minutesPlayed: number | null;
  goalBonusNormalPoints: number | null;
  goalBonusCaptainPoints: number | null;
}

export type PlayerMatch = {
  matchId: string;
  competitionLabel: string;
  kickoffAt: string;
  venue: null | string;
  stats: MatchdayStats | null;
  status: 'fixture' | 'playing' | 'played';
  homeTeam: {
    name: string;
    shortName: null | string;
    flagKey: null | string;
    score: null | string;
  };
  awayTeam: {
    name: string;
    shortName: null | string;
    flagKey: null | string;
    score: null | string;
  };
};

export interface BEPlayerDetail extends BEPlayerListItem {
  gamesPlayed: number;
  bestPerformance: number;
  nextFixtures: NextFixture[];
  matchdayStats?: MatchdayStats;
  playerMatches?: PlayerMatch[];
}

export type PlayerDetailResponse = ApiEnvelope<BEPlayerDetail>;

export type PlayersListResponse = ApiEnvelope<ApiCollection<BEPlayerListItem>>;

export interface PlayerMatchHistoryStat {
  label: string;
  total: string | number;
  points: string | number;
}

export interface PlayerMatchHistoryTeam {
  name: string;
  shortName: string;
  flagKey: string;
  score: number | null;
}

export interface PlayerMatchHistoryItem {
  matchId: string;
  competitionLabel: string;
  kickoffAt: string;
  venue: string;
  status: string;
  statusVariant: 'finished' | 'live';
  mdLabel: string;
  mdPoints: string;
  periodId: string;
  leftTeam: PlayerMatchHistoryTeam;
  rightTeam: PlayerMatchHistoryTeam;
  stats: PlayerMatchHistoryStat[];
  matchDetailAvailable: boolean;
}

export interface PlayerMatchHistoryData {
  items: PlayerMatchHistoryItem[];
}

export type PlayerMatchHistoryResponse = ApiEnvelope<PlayerMatchHistoryData>;

export interface FetchPlayerParams {
  playerId: string;
  periodId?: string;
  periodType: 'gameweek' | 'matchday';
  fullFixtures?: boolean;
  limitFixture?: number;
}

export const fetchPlayer = ({
  playerId,
  periodId,
  periodType = 'matchday',
  fullFixtures,
  limitFixture,
}: FetchPlayerParams): Promise<PlayerDetailResponse> => {
  return networkClient
    .get<PlayerDetailResponse>(`/api/players/${playerId}`, {
      params: {
        type: periodType,
        ...(periodType === 'gameweek' ? { gameweek_id: periodId } : { matchday_id: periodId }),
        ...(fullFixtures !== undefined ? { full_fixtures: fullFixtures } : {}),
        ...(limitFixture !== undefined ? { limit_fixture: limitFixture } : {}),
      },
    })
    .then(({ data }) => data);
};

export const fetchPlayers = (
  tournamentCalendarId?: string,
  type: 'gameweek' | 'matchday' = 'matchday',
  userId?: string | null,
  limitFixture?: number,
  preGw?: boolean
): Promise<PlayersListResponse> => {
  const params: Record<string, string> = {};
  if (tournamentCalendarId) params.tournamentCalendarId = tournamentCalendarId;
  params.type = type;
  if (userId) params.userId = userId;
  if (limitFixture) params.limit_fixture = String(limitFixture);
  if (preGw) params.pre_gw = 'true';
  return networkClient
    .get<PlayersListResponse>('/api/players', { params })
    .then(({ data }) => data);
};

export const fetchPlayerMatches = (
  playerId: string,
  type: 'gameweek' | 'matchday' = 'matchday'
): Promise<PlayerMatchHistoryResponse> => {
  return networkClient
    .get<PlayerMatchHistoryResponse>(`/api/players/${playerId}/matches`, {
      params: { type },
    })
    .then(({ data }) => data);
};
